import {
  isRehabFormType,
  REHAB_FORM_NAMES,
  SEARCH_ACTION_GENERATE_FORM,
  TRACK_EVENTS,
} from "core/consts";
import { useIsDocsAndExtractActive } from "core/model/careseekers";
import { getErrorMessage } from "core/model/utils/errors";
import { Auction, AuctionForm, Patient } from "core/types";
import { useConfirmationDialog } from "ds_legacy/components/ConfirmationDialog/useConfirmationDialog";
import { useToast } from "dsl/atoms/ToastNotificationContext";
import { FORM_NAME_TO_FILE_TYPE } from "dsl/ecosystems/RecareDocs/Modal/utils";
import { scrollToTop } from "dsl/hooks/useScrollTo";
import useSearchAction from "dsl/hooks/useSearchAction";
import { CircleHelpIcon } from "lucide-react";
import { resolveArhVersion } from "onprem/resolveArhVersion";
import { useCallback } from "react";
import { convertEncryptedFieldOut } from "react-forms-state";
import { useTracking } from "react-tracking";
import { useTranslations } from "translations";
import {
  getFormInputValue,
  getMedicalFormApproved,
  hasGkvInsuranceIkProfileMismatch,
} from "./utils";

export function useSyncGkvInsuranceIkFromProfile({
  auction,
  auctionForms,
  patient,
  refetchAuctionForms,
}: {
  auction: Auction;
  auctionForms: AuctionForm[];
  patient: Patient;
  refetchAuctionForms?: () => Promise<unknown>;
}) {
  const { trackEvent } = useTracking();
  const translations = useTranslations();
  const [confirmProps, confirm] = useConfirmationDialog();
  const [generateForm, generateFormProgress, resetProgress] = useSearchAction({
    actionType: SEARCH_ACTION_GENERATE_FORM,
  });
  const toast = useToast();
  const { isRedesignActive } = useIsDocsAndExtractActive();

  const syncFromProfile = useCallback(async () => {
    if (
      !auction ||
      !hasGkvInsuranceIkProfileMismatch({ auctionForms, patient })
    ) {
      return;
    }

    const rawProfile = patient.profile?.financing?.insurance?.ik_number;
    const profileIk =
      rawProfile == null || String(rawProfile).trim() === ""
        ? null
        : String(rawProfile).trim();
    if (!profileIk) {
      return;
    }

    const isMedicalFormApproved = getMedicalFormApproved(auctionForms);
    const { formData: medicalFormData } = getFormInputValue({
      auctionForms,
      formName: REHAB_FORM_NAMES.GKV_MEDICAL,
    });

    if (isMedicalFormApproved && !isRedesignActive && medicalFormData != null) {
      const confirmed = await confirm({
        cancelText:
          translations.patientForms.medicalForm.approvalConfirmation
            .buttonCancel,
        content:
          translations.patientForms.medicalForm.approvalConfirmation
            .confirmationModalText,
        headerIcon: (
          <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary-light">
            <CircleHelpIcon aria-hidden className="h-8 w-8 text-primary-dark" />
          </span>
        ),
        id: "save-and-overwrite",
        submitText:
          translations.patientForms.medicalForm.approvalConfirmation.buttonSave,
        title:
          translations.patientForms.medicalForm.approvalConfirmation
            .confirmationModalTitle,
        useNewDesign: true,
      });

      if (!confirmed) {
        return;
      }
    }

    const resolvedSchemaVersion = resolveArhVersion(auctionForms);
    const gkvFormNames = [
      REHAB_FORM_NAMES.GKV_GENERAL,
      REHAB_FORM_NAMES.GKV_MEDICAL,
    ] as const;

    for (const formName of gkvFormNames) {
      const { formData } = getFormInputValue({ auctionForms, formName });
      if (!formData) {
        continue;
      }

      const patched = { ...formData, insurance_ik_number: profileIk };
      const fileType = FORM_NAME_TO_FILE_TYPE[formName];

      const updatedAuction = {
        ...auction,
        forms: [
          {
            form_data: convertEncryptedFieldOut(JSON.stringify(patched)),
            form_type: fileType,
            ...(resolvedSchemaVersion && {
              schema_version: resolvedSchemaVersion,
            }),
            ...(resolvedSchemaVersion && {
              metadata: { schema_version: resolvedSchemaVersion },
            }),
          },
        ],
      };

      const generated = await generateForm({
        auction,
        context: {
          auction: updatedAuction,
          form_type: fileType,
        },
        onCompleted: (generatedAuction) => {
          const returnedForms = generatedAuction?.forms?.filter((form) =>
            isRehabFormType(form.form_type),
          );

          if (!returnedForms?.length) {
            throw new Error("no rehab form returned on sync insurance IK");
          }

          trackEvent({
            name: TRACK_EVENTS.REHAB_FORM_SAVE_AND_OVERWRITE_PDF_SUCCESS,
          });
          resetProgress();
        },
        onError: (err) => {
          trackEvent({
            name: TRACK_EVENTS.REHAB_FORM_SAVE_AND_OVERWRITE_PDF_ERROR,
          });
          console.error(
            `error syncing GKV insurance IK from profile - ${getErrorMessage(
              err,
            )}`,
          );
          toast({
            color: "danger",
            message: translations.auctionRequest.tryAgain,
          });
        },
      });

      if (!generated) {
        return;
      }
    }

    await refetchAuctionForms?.();

    if (!isRedesignActive) {
      toast({
        action: {
          label: translations.patientForms.transitionalCareForm.toTheTop,
          onClick: () => scrollToTop(),
        },
        color: "success",
        message: translations.patientForms.transitionalCareForm.pdfCreated,
      });
    }
  }, [
    auction,
    auctionForms,
    confirm,
    generateForm,
    isRedesignActive,
    patient,
    refetchAuctionForms,
    resetProgress,
    toast,
    trackEvent,
    translations,
  ]);

  return {
    confirmProps,
    syncFromProfile,
    syncProgress: generateFormProgress,
  };
}
